import React, {useState} from "react";
import ReactDOM from "react-dom";
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import styles from './PatientEnrollmentPopUp.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { setEnrollmentState, setReenrollment } from "./PatientEnrollmentSlice";
import { mergeModalitiesAndSerialNumbers } from './SavedModalitiesSlicer';
import { savePatientEnrollmentData } from '../../utils/enrollmentUtils';
import { customStyles } from "../../components/AccountManagement/CustomStyles";
import { UserProfilePictureIcon, XOut } from "../../components/icons/Icons";
import { showAlert } from "../../components/PerinHealthAlertSlice";
import useAxios from "../../services/AxiosConfig";
import modelStyles from '../../components/PerinHealthModel.module.css';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';


const PatientEnrollmentPopUp = ({type, item, onClose}) => {
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const labels = {
        remove: {
            title: "Discharge",
            buttonLabel: "Discharge"
        },
        reenroll: {
            title: "Re-enroll in a Care Program",
            buttonLabel: "Re-enroll"
        }
    };

    const dispatch = useDispatch();
    const [page, setPage] = useState("selecting");
    const [displayMessage, setDisplayMessage] = useState("");
    
    // use previouslyEnrolledSelection if re-enrolling
    const programs = (type === "reenroll" && item.previouslyEnrolledSelection.length !== 0) ? item.previouslyEnrolledSelection.map((program)=> (
        {value:program.id, label:program.name})) : item.selectOptions.map((program)=> (
            {value:program.id, label:program.name}));
    const [selectedPrograms, setSelectedPrograms] = useState([]);
    
    const navigate = useNavigate();

    const onActionHandler = (e) => {
        e.preventDefault();
        if (type === "remove") {
            if (page==="selecting") {
                if (selectedPrograms.length===programs.length) {
                    setDisplayMessage(`Are you sure you want to remove all programs for ${item.name}? By removing all programs, you will unenroll this patient.`);
                } else {
                    let displayNames = "";
                    if (selectedPrograms.length >= 2) {
                        displayNames = `${selectedPrograms.length} programs`;
                    } else {
                        displayNames = `"${selectedPrograms[0].label}" program`
                    }
                    setDisplayMessage(`Are you sure you want to remove ${displayNames} for ${item.name}?`);
                }
                setPage("confirming");
            } else {
                const deletePromises = selectedPrograms.map((program) => {
                    return httpService.delete(`enrollment/${program.value}/${item.patientId}`);
                });

                Promise.all(deletePromises)
                    .then((responses) => {
                        // Log successful discharge with patient and program details
                        const patientName = item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unknown Patient';
                        const programNames = selectedPrograms.map(program => program.label).join(', ');
                        const customMessage = `Patient discharged: ${patientName} from program(s): ${programNames}`;
                        
                        Logger.shared.info(
                            LogElements.gridEllipsesButton,
                            LogAction.clicked,
                            'PatientEnrollmentPopUp.js',
                            customMessage
                        );
                        
                        dispatch(
                            showAlert({
                                header: "Patient Discharged",
                                message: "",
                                type: 'success'
                            })
                        );
                        // Only refresh grid after successful discharge
                        onClose(true);
                    })
                    .catch(() => {
                        dispatch(
                            showAlert({
                                header: "Error",
                                message: "There was an error discharging from this program",
                                type: 'danger'
                            })
                        );
                        // Close without refreshing
                        onClose();
                    });
            }
        } else {
            if (type==='reenroll') {
                handleReenroll();
            } else {
                return;
            }
        }
    };

    const handleReenroll = async () => {
        const data = item.data;
        const enrollment = data.find((enrollment)=> enrollment.enrollmentId === selectedPrograms.value);
        const response = await httpService.get(`enrollment/${enrollment.enrollmentId}`);
        const reenrollmentInfo = response.data?.data[0];
        const { currentModalities, currentSerialNumbers } = savePatientEnrollmentData([reenrollmentInfo]);
        dispatch(mergeModalitiesAndSerialNumbers({ modalities: currentModalities, serialNumbers: currentSerialNumbers }));
        dispatch(setEnrollmentState(reenrollmentInfo));
        dispatch(setReenrollment());
        navigate('/enroll-patient');
        onClose();
    }

    return ReactDOM.createPortal(
        <div className={styles.PopupOverlay}>
            <div className={styles.PopupContainer}>
                <div onClick={()=>onClose()} className={styles.XOut}>
                    <XOut />
                </div>
                <div className={styles.PopupTitle}>
                    {labels[type].title}
                </div>
                {page==="confirming" &&
                    <div className={styles.PopupMessage}>
                        {displayMessage}
                    </div>
                }
                {page === 'selecting' && type==='remove' &&
                <div style={{display:"flex", flexDirection:"column", gap:"10px"}}>
                    <span className={styles.PopupMessage}>Please verify patient discharge details:</span>
                    <div style={{display:"flex", columnGap:"10px", alignItems:"center"}}>
                        <div style={{display:"flex", alignItems:"center"}}>
                            <span className={styles.InitialsIcon}>{`${item.firstName[0]}${item.lastName[0]}`}</span>
                            <UserProfilePictureIcon />
                        </div>
                        {item.name}      
                    </div>
                </div>
                }
                {page==='selecting' && 
                    <Select
                        options={programs}
                        styles={customStyles.addressInput}
                        className="dropdown-input"
                        placeholder='Select a program'
                        isSearchable={true}
                        isMulti={type==="remove"}
                        onChange={(selected) => setSelectedPrograms(selected)}
                    />
                }
                <div style={{display:"flex",justifyContent:"space-between", width:"100%", alignSelf:"center", borderTop:"1px solid #B9B9B9", paddingTop:"10px"}}>
                    <button onClick={()=>onClose()} className={modelStyles.SecondaryButton}>
                        Cancel
                    </button>
                    <button onClick={onActionHandler} disabled={selectedPrograms.length === 0} className={modelStyles.ModelButton}>
                        {labels[type].buttonLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default PatientEnrollmentPopUp;